/**
 * Global API fetch wrapper with automatic token refresh on 401.
 * All service files should use this instead of raw fetch.
 */
const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

let refreshPromise: Promise<RefreshOutcome> | null = null;
// Circuit breaker: dopo refresh fallito, blocca fetch successive per N ms
// per evitare raffiche infinite di 401 da componenti che si remontano in loop.
let authBrokenUntil = 0;
const AUTH_BROKEN_BACKOFF_MS = 5000;

function isLoginPath(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p === '/login' || p.startsWith('/partner-login');
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  if (isLoginPath()) return;
  // Path-based routing: redirect same-origin verso /login (Next renderizza
  // /login per tutte le aree /admin /partner /vendor /excursion). Usando
  // window.location.origin evitiamo redirect cross-domain bloccati dal browser
  // in contesti iframe ("Unsafe attempt to load URL ... from frame with URL").
  const returnUrl = window.location.pathname + window.location.search + window.location.hash;
  const url = new URL('/login', window.location.origin);
  if (returnUrl && returnUrl !== '/') url.searchParams.set('returnUrl', returnUrl);
  window.location.href = url.toString();
}

type RefreshOutcome = 'ok' | 'anonymous' | 'broken';

async function doRefresh(): Promise<RefreshOutcome> {
  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return 'broken';
    // Endpoint /auth/refresh ritorna 200 {success:false, code:'NO_REFRESH_TOKEN'}
    // per visitatori anonimi: NON è auth rotta, è semplice assenza di sessione.
    // Distinguere 'anonymous' da 'broken' evita di redirigere a /login utenti
    // mai loggati che colpiscono per sbaglio un endpoint protetto.
    const data = await res.json().catch(() => null);
    if (data && data.success === false) return 'anonymous';
    return 'ok';
  } catch {
    return 'broken';
  }
}

/**
 * Fetch with credentials + auto-refresh on 401.
 * Deduplicates concurrent refresh calls.
 *
 * Circuit breaker: dopo che un refresh fallisce, le fetch successive per
 * AUTH_BROKEN_BACKOFF_MS lanciano subito un errore senza colpire la rete e
 * redirezionano al login. Evita raffiche di 401 da componenti che si
 * remontano (auth scaduta + parent layout che ri-renderizza in loop).
 */
export async function apiFetch<T = any>(url: string, options?: RequestInit): Promise<T> {
  // Circuit breaker attivo: short-circuit + redirect
  if (Date.now() < authBrokenUntil) {
    redirectToLogin();
    throw new Error('Sessione scaduta. Effettua di nuovo il login.');
  }

  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  const opts: RequestInit = {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  };

  let res = await fetch(fullUrl, opts);

  // On 401, try refresh once then retry
  if (res.status === 401) {
    // Deduplicate: if a refresh is already in progress, wait for it
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
    }
    const outcome = await refreshPromise;
    if (outcome === 'ok') {
      res = await fetch(fullUrl, opts);
    } else if (outcome === 'broken') {
      // Refresh con sessione presente ma rotta → arma circuit breaker + redirect login
      authBrokenUntil = Date.now() + AUTH_BROKEN_BACKOFF_MS;
      redirectToLogin();
    }
    // outcome === 'anonymous' → propaga 401 al chiamante (caller decide se
    // mostrare placeholder login o ignorare). Niente redirect: utente mai
    // loggato che ricarica una pagina pubblica non deve finire su /login.
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(`Errore del server (${res.status})`);
  }
  const data = await res.json();
  if (!res.ok) {
    const err = new ApiError(data.error || data.message || 'Request failed', res.status, data?.code);
    throw err;
  }
  return data;
}

/** Error subclass that preserves HTTP status + machine-readable `code` from backend.
 *  Use `if (e instanceof ApiError && e.code === 'KYC_DOC_REQUIRED') { ... }` in callers. */
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export { API_URL };
